/**
 * Live status for the "Working as intended" rule-recording job.
 *
 * Subscribes to the job's event stream (`GET /jobs/:jobId/stream`, the same
 * SSE transport workflow runs and the cache optimizer use — see
 * `useCacheOptimizerStream.ts`). The rule-recording agent's finish tool
 * (`record_result`) emits a job-specific `intended-behavior-result` event
 * carrying the outcome; the generic `status` event with `status: 'failed'`
 * is the failure signal (the issue itself is already closed by the time this
 * hook is subscribed — a failure here only means the rule wasn't recorded).
 *
 * @module hooks/useIntendedBehaviorStream
 */

import React from 'react';
import { API_URL } from '../config';

export type IntendedBehaviorRuleAction = 'matched' | 'added' | 'updated' | 'proposed';

export interface IntendedBehaviorResult {
  action: IntendedBehaviorRuleAction;
  ruleId: string;
  statement: string;
  proposedStatement: string | null;
  capabilityId: string;
  capabilityCode: string | null;
  capabilityTitle: string;
}

export type IntendedBehaviorStreamStatus = 'recording' | 'landed' | 'failed' | 'timeout';

export interface IntendedBehaviorStreamState {
  status: IntendedBehaviorStreamStatus;
  result: IntendedBehaviorResult | null;
  /** Raw diagnostic detail — for console logging only, never rendered to the user. */
  errorMessage: string | null;
}

const INITIAL_STATE: IntendedBehaviorStreamState = {
  status: 'recording',
  result: null,
  errorMessage: null,
};

// The rule-recording job's own timeout is 180s; wait a bit past that before
// giving up on the client side and switching to a "still recording in the
// background" state rather than hanging on the spinner forever.
const CLIENT_TIMEOUT_MS = 210_000;

function isIntendedBehaviorResult(value: unknown): value is IntendedBehaviorResult {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.action === 'string' &&
    typeof record.ruleId === 'string' &&
    typeof record.statement === 'string' &&
    typeof record.capabilityId === 'string' &&
    typeof record.capabilityTitle === 'string'
  );
}

/**
 * Subscribes to `jobId`'s stream and reduces it to a single terminal
 * outcome. Pass `null` to stay idle (e.g. before the enqueue call returns).
 */
export function useIntendedBehaviorStream(jobId: string | null): IntendedBehaviorStreamState {
  const [state, setState] = React.useState<IntendedBehaviorStreamState>(INITIAL_STATE);

  React.useEffect(() => {
    if (!jobId) {
      setState(INITIAL_STATE);
      return;
    }
    setState(INITIAL_STATE);

    // True once a terminal outcome (result, failure, or timeout) has been
    // reached, so a late-arriving frame after the timeout fires can't
    // resurrect a stale "recording" render.
    let settled = false;

    const eventSource = new EventSource(`${API_URL}/jobs/${jobId}/stream`, {
      withCredentials: true,
    });

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setState({ status: 'timeout', result: null, errorMessage: null });
      eventSource.close();
    }, CLIENT_TIMEOUT_MS);

    const finish = (next: IntendedBehaviorStreamState) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      setState(next);
      eventSource.close();
    };

    const onResult = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as unknown;
        if (isIntendedBehaviorResult(data)) {
          finish({ status: 'landed', result: data, errorMessage: null });
        }
      } catch {
        /* ignore malformed frame */
      }
    };

    const onStatus = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as { status?: string; error?: string };
        if (data.status === 'failed') {
          finish({
            status: 'failed',
            result: null,
            errorMessage: data.error ?? 'Job reported status: failed',
          });
        } else if (data.status === 'canceled') {
          finish({
            status: 'failed',
            result: null,
            errorMessage: 'The rule-recording job was canceled.',
          });
        } else if (data.status === 'completed') {
          // The job finished without ever emitting its result — the finish
          // tool didn't run (or its event was dropped). Treat as a failure
          // rather than leaving the dialog spinning forever.
          finish({
            status: 'failed',
            result: null,
            errorMessage: 'Job completed without emitting an intended-behavior-result event',
          });
        }
      } catch {
        /* ignore malformed frame */
      }
    };

    const onError = (event: MessageEvent) => {
      // The stream route sends a named `error` event on a stream-level fault
      // (not to be confused with EventSource's own connection `error` event,
      // which carries no `.data` and is left to auto-reconnect).
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data) as { error?: string };
        finish({
          status: 'failed',
          result: null,
          errorMessage: data.error ?? 'Stream error event with no message',
        });
      } catch {
        finish({ status: 'failed', result: null, errorMessage: 'Unparseable stream error event' });
      }
    };

    eventSource.addEventListener('intended-behavior-result', onResult);
    eventSource.addEventListener('status', onStatus);
    eventSource.addEventListener('error', onError);

    return () => {
      settled = true;
      window.clearTimeout(timeoutId);
      eventSource.close();
    };
  }, [jobId]);

  return state;
}
